import React, {useEffect, useMemo, useRef, useState} from 'react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import MuiTooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {EndNodeSeverityDetail, SEVERITY_LABELS} from '../../../../utils/severity-suggestion';

export type OperationType = 'commercial' | 'general';
export type RiskMatrixMode = 'weighted' | 'multi-cell';

interface RiskMatrixProps {
    totalProbability: number;
    topNodeUniqueId?: string;
    topNodeName?: string;
    onSeverityChange?: (severity: number) => void;
    onOperationTypeChange?: (type: OperationType) => void;
    suggestedSeverity?: number;
    suggestedLikelihood?: number;
    endNodeDetails?: EndNodeSeverityDetail[];
}

type RiskLevel = 'H' | 'M' | 'L';
const RISK_BG: Record<RiskLevel, string> = {H: 'rgba(211,47,47,0.75)', M: '#ffff00', L: 'rgba(76,175,80,0.45)'};
const RISK_LABELS_MAP: Record<RiskLevel, string> = {H: 'High', M: 'Medium', L: 'Low'};
const RISK_COLORS: Record<RiskLevel, string> = {H: '#d32f2f', M: '#f9a825', L: '#4caf50'};

const LIKELIHOOD_ROWS: Array<{label: string; letter: string; minP: number}> = [
    {label: 'Frequent', letter: 'A', minP: 1e-5},
    {label: 'Infrequent', letter: 'B', minP: 1e-7},
    {label: 'Extremely Infrequent', letter: 'C', minP: 1e-8},
    {label: 'Remote', letter: 'D', minP: 1e-9},
    {label: 'Extremely Remote', letter: 'E', minP: 1e-10},
    {label: 'Improbable', letter: 'F', minP: 1e-11},
    {label: 'Extremely Improbable', letter: 'G', minP: 0},
];

const COMMERCIAL_MATRIX: RiskLevel[][] = [
    ['L', 'M', 'H', 'H', 'H'], // A
    ['L', 'M', 'H', 'H', 'H'], // B
    ['L', 'M', 'H', 'H', 'H'], // C
    ['L', 'M', 'M', 'H', 'H'], // D
    ['L', 'L', 'M', 'M', 'H'], // E
    ['L', 'L', 'L', 'M', 'M'], // F
    ['L', 'L', 'L', 'L', 'L'], // G
];

const GENERAL_AVIATION_MATRIX: RiskLevel[][] = [
    ['L', 'M', 'H', 'H', 'H'], // A
    ['L', 'M', 'M', 'H', 'H'], // B
    ['L', 'L', 'M', 'M', 'H'], // C
    ['L', 'L', 'L', 'M', 'M'], // D
    ['L', 'L', 'L', 'L', 'L'], // E
    ['L', 'L', 'L', 'L', 'L'], // F
    ['L', 'L', 'L', 'L', 'L'], // G
];

const RISK_MATRICES: Record<OperationType, RiskLevel[][]> = {
    commercial: COMMERCIAL_MATRIX,
    general: GENERAL_AVIATION_MATRIX,
};

const SEVERITY_HEADERS = ['Minimal', 'Minor', 'Major', 'Hazardous', 'Catastrophic'];

const SEVERITY_DEFINITIONS: Record<string, string> = {
    Minimal: '(5) Negligible safety effect.',
    Minor: '(4) An expected unintentional effect that includes any of: 1–2 minor injuries; minor damage to manned aircraft; substantial damage to unmanned aircraft weighing at least 55 pounds.',
    Major: '(3) An expected unintentional effect that includes any of: 1–2 serious injuries; 3 or more minor injuries; substantial damage to manned aircraft; hull loss to unmanned aircraft weighing at least 55 pounds.',
    Hazardous: '(2) An expected unintentional effect that includes any of: 1–2 fatalities without manned aircraft hull loss; manned aircraft hull loss without fatalities; 3 or more serious injuries.',
    Catastrophic: '(1) An expected unintentional effect that includes any of: 3 or more fatalities; manned aircraft hull loss with at least 1 fatality.',
};

const OUTCOME_COLORS: Record<string, string> = {
    NEGATIVE: '#d32f2f',
    NEUTRAL: '#f9a825',
    POSITIVE: '#4caf50',
};

function getLikelihoodRow(p: number): number {
    for (let i = 0; i < LIKELIHOOD_ROWS.length; i++) {
        if (p >= LIKELIHOOD_ROWS[i].minP) return i;
    }
    return LIKELIHOOD_ROWS.length - 1;
}

function formatProbRange(ri: number): string {
    const row = LIKELIHOOD_ROWS[ri];
    if (ri === 0) return `P(top) ≥ ${row.minP.toExponential(0)}`;
    const prev = LIKELIHOOD_ROWS[ri - 1];
    if (row.minP === 0) return `P(top) < ${prev.minP.toExponential(0)}`;
    return `${row.minP.toExponential(0)} ≤ P(top) < ${prev.minP.toExponential(0)}`;
}

function cellTooltip(ri: number, ci: number, riskMatrix: RiskLevel[][]): string {
    const level = riskMatrix[ri][ci];
    const likelihood = LIKELIHOOD_ROWS[ri];
    const severity = SEVERITY_HEADERS[ci];
    return `${RISK_LABELS_MAP[level]} Risk — ${likelihood.label} likelihood (${formatProbRange(ri)}) with ${severity} severity`;
}

function formatProb(p: number): string {
    if (p === 0) return '0';
    if (p < 0.001) return p.toExponential(2);
    return p.toPrecision(3);
}

/** Build a map of (row,col) → list of END node indices that occupy that cell. */
function buildMultiCellMap(details: EndNodeSeverityDetail[]): Map<string, number[]> {
    const map = new Map<string, number[]>();
    for (let i = 0; i < details.length; i++) {
        const d = details[i];
        const key = `${d.likelihoodRow},${d.derivedSeverity}`;
        const list = map.get(key);
        if (list) {
            list.push(i);
        } else {
            map.set(key, [i]);
        }
    }
    return map;
}

const PULSE_KEYFRAMES = `
@keyframes riskMatrixPulse {
    0%, 100% { box-shadow: inset 0 0 0 2px #333; }
    50% { box-shadow: inset 0 0 6px 2px rgba(0,0,0,0.4); }
}`;

const RiskMatrix: React.FC<RiskMatrixProps> = ({totalProbability, topNodeUniqueId, topNodeName, onSeverityChange, onOperationTypeChange, suggestedSeverity, suggestedLikelihood, endNodeDetails}) => {
    const [selectedSeverity, setSelectedSeverity] = useState<number>(-1);
    const [operationType, setOperationType] = useState<OperationType>('commercial');
    const [mode, setMode] = useState<RiskMatrixMode>('multi-cell');
    const [showDetails, setShowDetails] = useState<boolean>(false);
    const userOverridden = useRef<boolean>(false);

    const hasEndNodes = endNodeDetails !== undefined && endNodeDetails.length > 0;

    // Apply suggested severity when it arrives (unless user has manually overridden)
    useEffect(() => {
        if (mode === 'weighted' && suggestedSeverity !== undefined && suggestedSeverity >= 0 && !userOverridden.current) {
            setSelectedSeverity(suggestedSeverity);
            onSeverityChange?.(suggestedSeverity);
        }
    }, [suggestedSeverity, onSeverityChange, mode]);

    const handleSeverityChange = (value: number): void => {
        userOverridden.current = true;
        setSelectedSeverity(value);
        onSeverityChange?.(value);
    };

    const handleOperationTypeChange = (value: OperationType): void => {
        setOperationType(value);
        onOperationTypeChange?.(value);
    };

    const handleModeChange = (newMode: RiskMatrixMode): void => {
        setMode(newMode);
        if (newMode === 'multi-cell') {
            // In multi-cell mode, clear the single severity selection
            setSelectedSeverity(-1);
            onSeverityChange?.(-1);
        } else if (newMode === 'weighted' && suggestedSeverity !== undefined && suggestedSeverity >= 0) {
            // Switching back to weighted: restore suggestion if not overridden
            if (!userOverridden.current) {
                setSelectedSeverity(suggestedSeverity);
                onSeverityChange?.(suggestedSeverity);
            }
        }
    };

    const matrix = RISK_MATRICES[operationType];

    // In weighted mode, use the weighted likelihood from END nodes when available;
    // fall back to totalProbability (MCS top event) otherwise.
    const activeRow = mode === 'weighted' && suggestedLikelihood !== undefined
        ? suggestedLikelihood
        : getLikelihoodRow(totalProbability);

    // Weighted mode: single active cell
    const activeCol = mode === 'weighted' && selectedSeverity >= 0 ? selectedSeverity : null;
    const activeLevel = activeCol !== null ? matrix[activeRow][activeCol] : null;

    // Multi-cell mode: map of occupied cells
    const multiCellMap = useMemo((): Map<string, number[]> => {
        if (mode !== 'multi-cell' || !endNodeDetails) return new Map();
        return buildMultiCellMap(endNodeDetails);
    }, [mode, endNodeDetails]);

    const isSuggested = suggestedSeverity !== undefined && suggestedSeverity >= 0;
    const isAutoSelected = mode === 'weighted' && isSuggested && !userOverridden.current;
    const isOverridden = mode === 'weighted' && isSuggested && userOverridden.current && selectedSeverity !== suggestedSeverity;

    return (
        <Box sx={{borderBottom: 1, borderColor: 'divider', pb: 2, mb: 1}}>
            <Box sx={{
                mx: 2, mt: 1, p: 2, borderRadius: 2,
                bgcolor: 'background.paper',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)',
                border: '1px solid',
                borderColor: 'divider',
            }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                        Risk Matrix (FAA Order 8040.4C)
                    </Typography>
                    {hasEndNodes && (
                        <FormControl size="small" sx={{minWidth: 140}}>
                            <Select
                                value={mode}
                                onChange={(e): void => handleModeChange(e.target.value as RiskMatrixMode)}
                                sx={{fontSize: '0.7rem', height: 24}}
                            >
                                <MenuItem value="multi-cell" sx={{fontSize: '0.7rem'}}>Per END Node</MenuItem>
                                <MenuItem value="weighted" sx={{fontSize: '0.7rem'}}>Weighted Average</MenuItem>
                            </Select>
                        </FormControl>
                    )}
                </Stack>
                {(topNodeUniqueId || topNodeName) && (
                    <Typography variant="caption" sx={{color: 'text.secondary', fontSize: '0.7rem', display: 'block'}}>
                        {topNodeUniqueId}{topNodeUniqueId && topNodeName ? ' — ' : ''}{topNodeName}
                    </Typography>
                )}

                <Stack direction="row" alignItems="center" spacing={1} sx={{mt: 1, mb: 1}} flexWrap="wrap">
                    <Typography variant="caption" sx={{fontSize: '0.7rem', fontWeight: 500}}>
                        Operations:
                    </Typography>
                    <FormControl size="small" sx={{minWidth: 150}}>
                        <Select
                            value={operationType}
                            onChange={(e): void => handleOperationTypeChange(e.target.value as OperationType)}
                            sx={{fontSize: '0.75rem', height: 28}}
                        >
                            <MenuItem value="commercial" sx={{fontSize: '0.75rem'}}>Commercial Aviation</MenuItem>
                            <MenuItem value="general" sx={{fontSize: '0.75rem'}}>General Aviation</MenuItem>
                        </Select>
                    </FormControl>
                    {mode === 'weighted' && (
                        <>
                            <Typography variant="caption" sx={{fontSize: '0.7rem', fontWeight: 500, ml: 1}}>
                                Severity:
                            </Typography>
                            <FormControl size="small" sx={{minWidth: 160}}>
                                <Select
                                    value={selectedSeverity}
                                    onChange={(e): void => handleSeverityChange(e.target.value as number)}
                                    sx={{fontSize: '0.75rem', height: 28}}
                                    displayEmpty
                                >
                                    <MenuItem value={-1} sx={{fontSize: '0.75rem', color: 'text.secondary'}}>
                                        <em>Select severity...</em>
                                    </MenuItem>
                                    {SEVERITY_HEADERS.map((label, idx) => (
                                        <MenuItem key={label} value={idx} sx={{fontSize: '0.75rem'}}>
                                            {label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {selectedSeverity >= 0 && (
                                <MuiTooltip title={SEVERITY_DEFINITIONS[SEVERITY_HEADERS[selectedSeverity]]} arrow placement="top">
                                    <Typography variant="caption" sx={{fontSize: '0.6rem', color: 'text.secondary', cursor: 'help'}}>
                                        (?)
                                    </Typography>
                                </MuiTooltip>
                            )}
                            {isAutoSelected && (
                                <Typography variant="caption" sx={{fontSize: '0.6rem', color: 'info.main', fontStyle: 'italic'}}>
                                    Auto-suggested from END nodes
                                </Typography>
                            )}
                            {isOverridden && suggestedSeverity !== undefined && suggestedSeverity >= 0 && (
                                <Typography variant="caption" sx={{fontSize: '0.6rem', color: 'text.secondary', fontStyle: 'italic'}}>
                                    Suggested: {SEVERITY_LABELS[suggestedSeverity]}
                                </Typography>
                            )}
                            {activeLevel && (
                                <Typography variant="caption" sx={{
                                    fontSize: '0.75rem', fontWeight: 700,
                                    color: RISK_COLORS[activeLevel],
                                    ml: 1,
                                }}>
                                    → {RISK_LABELS_MAP[activeLevel]} Risk
                                </Typography>
                            )}
                        </>
                    )}
                    {mode === 'multi-cell' && (
                        <Typography variant="caption" sx={{fontSize: '0.65rem', color: 'text.secondary', fontStyle: 'italic', ml: 1}}>
                            Each END node mapped to its own likelihood/severity cell
                        </Typography>
                    )}
                    {!hasEndNodes && (
                        <Typography variant="caption" sx={{fontSize: '0.65rem', color: 'warning.main', fontStyle: 'italic', ml: 1}}>
                            No END Nodes data available to derive severity from — Please, select severity manually
                        </Typography>
                    )}
                </Stack>

                <style>{PULSE_KEYFRAMES}</style>
                <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '0.6rem', tableLayout: 'fixed'}}>
                    <thead>
                        <tr>
                            <th style={{width: 14}} />
                            <th style={{width: 120}} />
                            <th colSpan={5} style={{textAlign: 'center', padding: '1px 2px', fontWeight: 600, color: '#666'}}>
                                Severity
                            </th>
                        </tr>
                        <tr>
                            <th style={{width: 14}} />
                            <th style={{width: 120, borderBottom: '1px solid #ccc'}} />
                            {SEVERITY_HEADERS.map((h, ci) => (
                                <MuiTooltip key={h} title={SEVERITY_DEFINITIONS[h]} arrow placement="top">
                                    <th style={{
                                        padding: 0, fontWeight: 600,
                                        color: activeCol === ci ? '#000' : '#666',
                                        borderBottom: '1px solid #ccc',
                                        height: 52, verticalAlign: 'bottom',
                                        position: 'relative', cursor: 'help',
                                    }}>
                                        <span style={{
                                            display: 'inline-block',
                                            transform: 'rotate(-45deg)',
                                            transformOrigin: 'bottom left',
                                            whiteSpace: 'nowrap',
                                            position: 'absolute',
                                            bottom: 2,
                                            left: '50%',
                                        }}>
                                            {h}
                                        </span>
                                    </th>
                                </MuiTooltip>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {LIKELIHOOD_ROWS.map((row, ri) => (
                            <tr key={row.letter}>
                                {ri === 0 && (
                                    <td rowSpan={LIKELIHOOD_ROWS.length} style={{
                                        width: 14, textAlign: 'center', verticalAlign: 'middle',
                                        fontWeight: 600, color: '#666', padding: 0,
                                    }}>
                                        <span style={{writingMode: 'vertical-rl', transform: 'rotate(180deg)', display: 'inline-block'}}>
                                            Likelihood
                                        </span>
                                    </td>
                                )}
                                <td style={{
                                    padding: '2px 3px',
                                    fontWeight: (mode === 'weighted' && ri === activeRow) ? 700 : 400,
                                    color: (mode === 'weighted' && ri === activeRow) ? '#000' : '#888',
                                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                }}>
                                    {row.letter} – {row.label}
                                </td>
                                {matrix[ri].map((level, ci) => {
                                    // Weighted mode: single active cell
                                    const isWeightedActive = mode === 'weighted' && activeCol !== null && ri === activeRow && ci === activeCol;

                                    // Multi-cell mode: check if any END nodes occupy this cell
                                    const cellKey = `${ri},${ci}`;
                                    const multiCellIndices = mode === 'multi-cell' ? (multiCellMap.get(cellKey) ?? []) : [];
                                    const isMultiActive = multiCellIndices.length > 0;

                                    const isActive = isWeightedActive || isMultiActive;

                                    // Build tooltip for multi-cell
                                    let tooltip: string;
                                    if (isMultiActive && endNodeDetails) {
                                        const labels = multiCellIndices.map((idx) => {
                                            const d = endNodeDetails[idx];
                                            return d.uniqueId ? `${d.uniqueId}: ${d.name}` : d.name;
                                        });
                                        tooltip = `${RISK_LABELS_MAP[level]} Risk — ${labels.join('; ')}`;
                                    } else {
                                        tooltip = cellTooltip(ri, ci, matrix);
                                    }

                                    // Cell label
                                    let cellLabel = '';
                                    if (isWeightedActive) {
                                        cellLabel = RISK_LABELS_MAP[level];
                                    } else if (isMultiActive) {
                                        cellLabel = multiCellIndices.length === 1
                                            ? `${multiCellIndices.length}`
                                            : `${multiCellIndices.length}`;
                                    }

                                    return (
                                        <MuiTooltip key={ci} title={tooltip} arrow placement="top">
                                            <td style={{
                                                textAlign: 'center', padding: '2px 0', cursor: 'help',
                                                backgroundColor: RISK_BG[level],
                                                color: isActive ? '#000' : level === 'H' ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.45)',
                                                fontWeight: isActive ? 800 : 400,
                                                border: isActive ? '2px solid #333' : '1px solid rgba(255,255,255,0.5)',
                                                animation: isActive ? 'riskMatrixPulse 2s ease-in-out infinite' : undefined,
                                            }}>
                                                {cellLabel}
                                            </td>
                                        </MuiTooltip>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
                <Stack direction="row" justifyContent="center" spacing={0.5} sx={{mt: 0.5, ml: '134px'}}>
                    {(['H', 'M', 'L'] as RiskLevel[]).map((level) => (
                        <Stack key={level} direction="row" alignItems="center" spacing={0.3}>
                            <Box sx={{width: 10, height: 10, bgcolor: RISK_BG[level], border: '1px solid rgba(0,0,0,0.15)' }} />
                            <Typography variant="caption" sx={{fontSize: '0.55rem', color: 'text.secondary'}}>
                                {RISK_LABELS_MAP[level]} Risk
                            </Typography>
                        </Stack>
                    ))}
                </Stack>

                {/* END Node Severity Derivation Details */}
                {hasEndNodes && (
                    <Box sx={{mt: 1.5, borderTop: 1, borderColor: 'divider', pt: 1}}>
                        <Typography
                            variant="caption"
                            onClick={(): void => setShowDetails(!showDetails)}
                            sx={{
                                fontSize: '0.65rem', color: 'info.main', cursor: 'pointer',
                                '&:hover': {textDecoration: 'underline'},
                            }}
                        >
                            {showDetails ? 'Hide' : 'Show'} severity derivation ({endNodeDetails!.length} END node{endNodeDetails!.length !== 1 ? 's' : ''})
                        </Typography>
                        <Collapse in={showDetails}>
                            <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '0.6rem', marginTop: 4}}>
                                <thead>
                                    <tr style={{borderBottom: '1px solid #ccc'}}>
                                        <th style={{textAlign: 'left', padding: '2px 4px', fontWeight: 600, color: '#666', width: 70}}>ID</th>
                                        <th style={{textAlign: 'left', padding: '2px 4px', fontWeight: 600, color: '#666'}}>END Node</th>
                                        <th style={{textAlign: 'center', padding: '2px 4px', fontWeight: 600, color: '#666', width: 60}}>Outcome</th>
                                        <th style={{textAlign: 'right', padding: '2px 4px', fontWeight: 600, color: '#666', width: 65}}>Probability</th>
                                        <th style={{textAlign: 'center', padding: '2px 4px', fontWeight: 600, color: '#666', width: 75}}>Severity</th>
                                        <th style={{textAlign: 'center', padding: '2px 4px', fontWeight: 600, color: '#666', width: 75}}>Likelihood</th>
                                        <th style={{textAlign: 'left', padding: '2px 4px', fontWeight: 600, color: '#666'}}>Reason</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {endNodeDetails!.map((d, i) => (
                                        <tr key={d.uniqueId || i} style={{borderBottom: '1px solid #eee', opacity: d.isPositive ? 0.5 : 1}}>
                                            <td style={{padding: '2px 4px', fontFamily: 'monospace', fontSize: '0.55rem', color: '#888'}}>
                                                {d.uniqueId}
                                            </td>
                                            <td style={{padding: '2px 4px', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                                                <MuiTooltip title={d.name} arrow placement="top">
                                                    <span>{d.name}</span>
                                                </MuiTooltip>
                                            </td>
                                            <td style={{textAlign: 'center', padding: '2px 4px', color: OUTCOME_COLORS[d.outcome] ?? '#666', fontWeight: 600}}>
                                                {d.outcome}
                                            </td>
                                            <td style={{textAlign: 'right', padding: '2px 4px', fontFamily: 'monospace'}}>
                                                {formatProb(d.probability)}
                                            </td>
                                            <td style={{textAlign: 'center', padding: '2px 4px', fontWeight: 600}}>
                                                {d.derivedSeverityLabel}
                                            </td>
                                            <td style={{textAlign: 'center', padding: '2px 4px', fontSize: '0.55rem'}}>
                                                {LIKELIHOOD_ROWS[d.likelihoodRow].letter} – {LIKELIHOOD_ROWS[d.likelihoodRow].label}
                                            </td>
                                            <td style={{padding: '2px 4px', color: '#888', fontSize: '0.55rem'}}>
                                                {d.reason}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {mode === 'weighted' && isSuggested && (
                                <Typography variant="caption" sx={{fontSize: '0.6rem', color: 'text.secondary', mt: 0.5, display: 'block', fontStyle: 'italic'}}>
                                    Weighted average: {suggestedSeverity !== undefined ? SEVERITY_LABELS[suggestedSeverity] : '—'} (probability-weighted across non-positive END nodes)
                                </Typography>
                            )}
                        </Collapse>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default RiskMatrix;
